import React, { Dispatch, SetStateAction, useState } from 'react'

import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Icon,
  Pressable,
  SH1,
  Select,
  Stack,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import useLeadFilterStore from '@zet/feature-leads/store/leadFilter'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { DEVICE_HEIGHT, QuickFilter } from '@zet/utils/Constants'
import { LabelObject } from '@zet/utils/interface'

type FilterModalProps = {
  filterOptions: LabelObject[]
  setIsBottomSheetVisible: Dispatch<SetStateAction<boolean>>
  filterName: string | undefined
  title: string | undefined
  filterIcon: string | undefined
}

const FilterModal = ({
  filterOptions,
  setIsBottomSheetVisible,
  filterName,
  title,
  filterIcon,
}: FilterModalProps) => {
  const leadFilterStore = useLeadFilterStore()
  const [localFilter, setLocalFilter] = useState<Array<string>>(
    leadFilterStore.leadFilters[filterName]
  )
  const { data: user } = useUserContext()

  const queryClient = useQueryClient()

  const handleFilterSelection = (value: string[]) => {
    setLocalFilter(value)
  }

  return (
    <Stack>
      <XStack
        className={`border-grays-30 items-center justify-between border-b px-3 py-2`}
      >
        <XStack className="items-center">
          <Icon type={filterIcon} size={24} />
          <SH1 className=" ml-[12] ">
            {i18n.t('myLeads.filterBy', { title })}
          </SH1>
        </XStack>
        <Pressable onPress={() => setIsBottomSheetVisible(false)}>
          <Icon size={24} type="Close" />
        </Pressable>
      </XStack>
      <BottomSheetScrollView
        style={{
          maxHeight: DEVICE_HEIGHT - 200,
        }}
      >
        <Stack className="px-3">
          <Select
            value={localFilter}
            mode="multi"
            onChange={handleFilterSelection}
          >
            {filterOptions?.map(({ value, label }, index) => (
              <Select.Option
                key={`${value}+${index}`}
                name={value}
                selectedValue={localFilter}
                optionContainerStyle="h-[62] mt-3 w-full justify-center rounded-lg"
                showToggle={true}
                backgroundColor={{
                  active: 'bg-[#CFE3FF]',
                  inActive: 'bg-[#F5F7FB]',
                }}
                iconColor={{
                  active: zetColors.primary[50],
                  inActive: zetColors.grays[60],
                }}
                textColor={{
                  active: 'text-grays-80',
                  inActive: 'text-grays-60',
                }}
                optionTextStyle="pl-3 pr-4 mr-3"
              >
                <SH1>{label}</SH1>
              </Select.Option>
            ))}
          </Select>
        </Stack>
      </BottomSheetScrollView>

      <Stack className="mt-4/2 border-grays-30 border-t px-3 py-3  ">
        <XStack className="justify-between">
          <Button
            className="bg-grays-20 mr-2 flex-grow"
            size={'lg'}
            textStyle="text-primary-100"
            onPress={() => {
              leadFilterStore.updateFilter({ [filterName]: [] })
              setIsBottomSheetVisible(false)
              QuickFilter[filterName].value = []
              queryClient.invalidateQueries({
                queryKey: ['leads'],
                refetchType: 'active',
              })
            }}
            eventName="click_action"
            eventPayload={{
              item_name: 'my_leads_clear_filters',
              user_id: user?.id,
            }}
          >
            {i18n.t('myLeads.clear')}
          </Button>
          <Button
            onPress={() => {
              setIsBottomSheetVisible(false)
              leadFilterStore.updateFilter({ [filterName]: localFilter })
              QuickFilter[filterName].value = localFilter
              queryClient.invalidateQueries({
                queryKey: ['leads'],
                refetchType: 'active',
              })
            }}
            className="ml-2 flex-grow"
            size={'lg'}
            eventName="click_action"
            eventPayload={{
              item_name: 'my_leads_apply_filters',
              user_id: user?.id,
              filter_by: filterName,
              option_selected: localFilter,
            }}
          >
            {i18n.t('myLeads.apply')}
          </Button>
        </XStack>
      </Stack>
    </Stack>
  )
}

export default FilterModal
